import { Suspense, lazy } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight, Cloud, Server, Shield, Zap, GitBranch, Bot, Container,
  Database, Cpu, Activity, Sparkles, CheckCircle2, Layers, Globe, Lock,
} from "lucide-react";

const HeroScene = lazy(() => import("@/components/three/HeroScene"));

const stats = [
  { value: "99.99%", label: "Uptime SLA" },
  { value: "500+", label: "Deployments shipped" },
  { value: "40%", label: "Avg. cloud cost cut" },
  { value: "24/7", label: "SRE coverage" },
];

const services = [
  { icon: Activity, title: "CloudOps", desc: "24/7 monitoring, incident response, and cost optimization across AWS, Azure, and GCP.", href: "/services#cloudops", tag: "Operate" },
  { icon: GitBranch, title: "DevOps", desc: "CI/CD, IaC, GitOps, and release automation — engineered for velocity and safety.", href: "/services#devops", tag: "Automate" },
  { icon: Container, title: "Kubernetes", desc: "EKS, AKS, GKE with service mesh, autoscaling, and production observability.", href: "/services#kubernetes", tag: "Modernize" },
  { icon: Cloud, title: "Migration", desc: "Zero-downtime moves from on-prem or between clouds. Assessed, planned, executed.", href: "/services#migration", tag: "Move" },
  { icon: Bot, title: "AI Agents", desc: "Production LLM agents deployed to your VPC. Own your data, your models, your workflows.", href: "/services#ai-agents", tag: "Deploy" },
  { icon: Database, title: "Implementation", desc: "Greenfield cloud architecture and multi-cloud data platforms, production-ready.", href: "/services#implementation", tag: "Build" },
];

const logos = ["FinStack", "HealthBridge", "DataPulse", "ScaleUp", "TechNova", "OrbitPay", "Cortex", "Nimbus"];

const pillars = [
  { icon: Shield, title: "Compliance-grade security", desc: "SOC 2, HIPAA, ISO 27001 patterns baked into every deployment." },
  { icon: Zap, title: "Sub-minute deploys", desc: "GitOps pipelines that ship 50+ times a week with zero downtime." },
  { icon: Globe, title: "Multi-cloud fluency", desc: "Vendor-neutral architectures across AWS, Azure, and GCP." },
  { icon: Lock, title: "Own your infrastructure", desc: "Everything runs in your accounts. No lock-in, no black boxes." },
];

const process = [
  { step: "01", title: "Assess", desc: "We audit your architecture, cost profile, and reliability baseline in the first two weeks." },
  { step: "02", title: "Design", desc: "A pragmatic reference architecture with clear trade-offs — no ivory-tower diagrams." },
  { step: "03", title: "Build", desc: "We ship in your accounts with IaC, GitOps, and full observability from day one." },
  { step: "04", title: "Operate", desc: "Ongoing SRE with named engineers. On-call rotation, monthly reviews, quarterly roadmaps." },
];

const testimonials = [
  { quote: "Cloudease turned a six-month migration into eight weeks, with zero customer-facing downtime.", name: "Rajesh Kumar", role: "CTO", company: "FinStack" },
  { quote: "Our deploy frequency went from monthly to daily. The team feels like our own SRE org.", name: "Priya Sharma", role: "VP Engineering", company: "HealthBridge" },
  { quote: "They didn't just cut our AWS bill — they taught us how to keep it low. Best investment this year.", name: "Arjun Mehta", role: "Founder", company: "DataPulse" },
];

const faqs = [
  { q: "How fast can you onboard?", a: "Most engagements start delivering value within two weeks. We run a paid two-week assessment, then move into build or operate mode." },
  { q: "Do you work with our existing team?", a: "Yes. We embed alongside your engineers — pairing, reviewing, and handing over runbooks. You keep the knowledge." },
  { q: "Which clouds do you cover?", a: "AWS, Azure, and GCP as primaries. We also support Cloudflare, Vercel, and self-managed Kubernetes on bare metal." },
  { q: "Do you sign SLAs?", a: "Yes. Managed services come with 24/7 coverage and defined response and resolution SLAs based on severity." },
  { q: "How is pricing structured?", a: "Fixed-fee assessments, milestone-based projects, and monthly retainers for managed services. Every proposal is transparent." },
];

const Home = () => {
  return (
    <Layout>
      <Helmet>
        <title>Cloudease — Enterprise Cloud & DevOps, Engineered for Scale</title>
        <meta name="description" content="Production-grade cloud infrastructure, DevOps automation, Kubernetes modernization, and AI agents on AWS, Azure, and GCP." />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center pt-16">
        <div className="absolute inset-0 bg-aurora animate-aurora" aria-hidden />
        <div className="absolute inset-0 bg-lines opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" aria-hidden />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" aria-hidden />

        <div className="container relative mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono mb-8"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-glow" />
              <span className="text-muted-foreground">Now available:</span>
              <span className="text-foreground">Custom AI Agents on your infra</span>
              <ArrowRight className="h-3 w-3" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="text-[clamp(2.5rem,6vw,5.5rem)] font-heading font-semibold leading-[1.02] tracking-tight text-foreground mb-6"
            >
              Cloud infrastructure,
              <br />
              <span className="text-gradient">engineered for scale.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed mb-10"
            >
              We design, deploy, and operate production-grade cloud platforms — so your team ships software, not tickets.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="flex flex-wrap gap-3"
            >
              <Link to="/contact">
                <Button variant="gradient" size="xl" className="rounded-full">
                  Book a consultation
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="glass" size="xl" className="rounded-full">
                  Explore services
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl"
            >
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="text-2xl md:text-3xl font-heading font-semibold text-foreground">{s.value}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* 3D scene */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="lg:col-span-5 h-[420px] md:h-[540px] relative"
          >
            <div className="absolute inset-0 bg-brand opacity-20 blur-[80px] rounded-full" aria-hidden />
            <Suspense fallback={<div className="h-full w-full glass rounded-2xl animate-pulse" />}>
              <HeroScene />
            </Suspense>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs font-mono text-muted-foreground/60 tracking-widest">
          SCROLL ↓
        </div>
      </section>

      {/* LOGO MARQUEE */}
      <section className="py-16 border-y border-border/60 bg-background relative overflow-hidden">
        <p className="text-center text-xs font-mono uppercase tracking-widest text-muted-foreground mb-10">
          Trusted by engineering teams from seed to Series C
        </p>
        <div className="marquee-mask overflow-hidden">
          <div className="flex gap-16 animate-marquee whitespace-nowrap w-max">
            {[...logos, ...logos].map((name, i) => (
              <div key={i} className="flex items-center gap-2 text-muted-foreground/70 font-heading font-medium text-lg">
                <div className="h-6 w-6 rounded-md bg-brand opacity-70" />
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_80%)]" aria-hidden />

        <div className="container relative mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">01 · Platform</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
              Every layer of the cloud, <span className="text-gradient">handled.</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              One team for CloudOps, DevOps, Kubernetes, migration, and AI. No handoffs, no context loss.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Link
                  to={s.href}
                  className="group relative block h-full p-7 rounded-2xl glass hover:border-primary/40 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="absolute inset-0 rounded-2xl bg-brand opacity-0 group-hover:opacity-10 transition-opacity" aria-hidden />
                  <div className="relative">
                    <div className="flex items-start justify-between mb-6">
                      <div className="h-11 w-11 rounded-xl bg-brand/10 border border-primary/20 flex items-center justify-center">
                        <s.icon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{s.tag}</span>
                    </div>
                    <h3 className="font-heading font-semibold text-xl text-foreground mb-2">{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">{s.desc}</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2.5 transition-all">
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS — dark strip */}
      <section className="relative py-32 border-y border-border/60 bg-secondary/30 overflow-hidden noise">
        <div className="absolute inset-0 bg-mesh opacity-30 animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">02 · Why us</p>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
                Built like the platforms we love.
              </h2>
              <p className="text-lg text-muted-foreground">
                Rigor of Stripe. Speed of Vercel. Trust of Cloudflare. We bring it to your infrastructure.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {pillars.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="p-6 rounded-2xl glass"
                >
                  <p.icon className="h-6 w-6 text-primary mb-4" />
                  <h3 className="font-heading font-semibold text-lg mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-32 relative">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">03 · Process</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-5">
              From audit to always-on, in weeks.
            </h2>
            <p className="text-lg text-muted-foreground">
              A predictable four-phase engagement. No surprises, no scope creep.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            <div className="hidden lg:block absolute top-6 left-8 right-8 h-px bg-gradient-to-r from-primary via-violet to-cyan" aria-hidden />
            {process.map((p, i) => (
              <motion.div
                key={p.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative"
              >
                <div className="h-12 w-12 rounded-full glass border-primary/40 flex items-center justify-center font-mono text-sm text-primary mb-5 relative z-10 bg-background">
                  {p.step}
                </div>
                <h3 className="font-heading font-semibold text-xl mb-2">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-32 border-t border-border/60 relative overflow-hidden">
        <div className="absolute inset-0 bg-aurora opacity-40 animate-aurora" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">04 · Customers</p>
            <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight">
              Teams don't outgrow us — <span className="text-gradient">they scale with us.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.figure
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-8 rounded-2xl glass hover:border-primary/40 transition-colors"
              >
                <Sparkles className="h-5 w-5 text-primary mb-6" />
                <blockquote className="text-foreground leading-relaxed mb-6 text-lg">"{t.quote}"</blockquote>
                <figcaption className="pt-4 border-t border-border/60">
                  <div className="font-medium text-foreground">{t.name}</div>
                  <div className="text-sm text-muted-foreground">{t.role}, {t.company}</div>
                </figcaption>
              </motion.figure>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all">
              Read customer stories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32 border-t border-border/60">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-4">05 · Answers</p>
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight mb-12">
            Frequently asked.
          </h2>

          <div className="space-y-3">
            {faqs.map((f, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="group rounded-xl glass p-5 open:border-primary/40"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none font-heading font-medium text-foreground">
                  {f.q}
                  <span className="h-6 w-6 rounded-full border border-border flex items-center justify-center text-lg text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 overflow-hidden border-t border-border/60">
        <div className="absolute inset-0 bg-mesh opacity-40 animate-aurora" aria-hidden />
        <div className="absolute inset-0 bg-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_80%)]" aria-hidden />
        <div className="container relative mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-mono mb-8">
              <Cpu className="h-3 w-3 text-primary" /> Ready when you are
            </div>
            <h2 className="text-4xl md:text-6xl font-heading font-semibold tracking-tight mb-6">
              Ship faster. <br /><span className="text-gradient">Sleep better.</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto">
              A 30-minute conversation is usually enough to know if we're the right fit.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact">
                <Button variant="gradient" size="xl" className="rounded-full">
                  Book a consultation <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="glass" size="xl" className="rounded-full">See services</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
